import httpClient from "../httpClient";

export interface Audience {
  id: string;
  name: string;
  userCount: number;
  checked: boolean;
  role?: number;
  department?: string;
}

export interface AudienceUser {
  uuid: string;
  name: string;
  email: string;
  roleId: number;
}

export interface NotificationRecord {
  id: string;
  uuid?: string;
  title: string;
  subtitle: string;
  type: 'email' | 'in-app' | 'both';
  audiences: string[];
  status: 'sent' | 'scheduled' | 'draft' | 'sending' | 'failed';
  date: string;
  dateLabel: string;
  openRate?: number;
  clickRate?: number;
  message: string;
  targetAudiences: Array<{ name: string; color: string }>;
  sentDate: string;
  scheduledDate?: string;
  scheduledForRaw?: string;
  roles?: number[];
  specificUserIds?: string[];
  specificEmails?: string[];
}

// Format a UTC/ISO timestamp into a readable IST (Asia/Kolkata) date-time.
export const formatDateIST = (value?: string | null): string => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return typeof value === 'string' ? value : '';
  return d.toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

export interface CampaignData {
  title: string;
  subject: string;
  message: string;
  targetAudience: {
    roles?: number[];
    departments?: string[];
    specificUsers?: string[];
    specificEmails?: string[];
  };
  deliveryMethod: 'email' | 'in-app' | 'both';
  scheduledFor?: string | null;
  // 'draft' keeps it a draft even if a schedule date is attached.
  status?: 'draft' | 'scheduled';
}

const ENDPOINTS = {
  CAMPAIGNS: "/super-admin/marketing/campaigns",
  SEND_CAMPAIGN: (uuid: string) => `/super-admin/marketing/campaigns/${uuid}/send`,
  VALIDATE_EMAILS: "/super-admin/marketing/validate-emails",
};

export const uploadMarketingImage = async (file: File): Promise<{
  success: boolean; url?: string; filename?: string; error?: { message: string } | null;
}> => {
  try {
    const formData = new FormData();
    formData.append('image', file);
    const response = await httpClient.post<any>('/content/uploads/marketing/image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to upload image' } };
  }
};

export const uploadMarketingAttachment = async (file: File): Promise<{
  success: boolean; url?: string; filename?: string; originalName?: string; error?: { message: string } | null;
}> => {
  try {
    const formData = new FormData();
    formData.append('attachment', file);
    const response = await httpClient.post<any>('/content/uploads/marketing/attachment', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to upload attachment' } };
  }
};

export const getUsersByRole = async (): Promise<{
  success: boolean;
  data?: AudienceUser[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<any>('/super-admin/marketing/users-by-role');
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch users' } };
  }
};

export const validateEmails = async (emails: string[]): Promise<{
  success: boolean;
  data?: { validEmails: string[]; invalidEmails: string[] };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<any>(ENDPOINTS.VALIDATE_EMAILS, { emails });
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to validate emails' } };
  }
};

// Mock audience list - used for UI dropdown selection
const mockAudiences: Audience[] = [
  { id: '1', name: 'All Trainees', userCount: 150, checked: false, role: 1 },
  { id: '2', name: 'All Instructors', userCount: 25, checked: false, role: 2 },
  { id: '3', name: 'All Admins', userCount: 5, checked: false, role: 3 },
];

export const getAudiences = async (): Promise<{
  success: boolean;
  data?: Audience[];
  error?: { message: string } | null;
}> => {
  try {
    return {
      success: true,
      data: mockAudiences,
      error: null
    };
  } catch (error: any) {
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch audiences' }
    };
  }
};

export const getNotificationHistory = async (): Promise<{
  success: boolean;
  data?: NotificationRecord[];
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<any>(ENDPOINTS.CAMPAIGNS);

    if (response.success && response.data) {
      const roleName = (r: number) => (r === 1 ? 'Trainees' : r === 2 ? 'Instructors' : 'Admins');

      const records: NotificationRecord[] = response.data.map((campaign: any) => {
        // The stored procedure returns camelCase aliases (targetAudience, sentAt,
        // totalOpened, …); older/raw paths may use snake_case. Read both. The
        // target audience is a JSON column that may arrive as an object OR a
        // string depending on the driver, so normalize it safely.
        const rawTa = campaign.targetAudience ?? campaign.target_audience;
        let ta: any = {};
        try { ta = typeof rawTa === 'string' ? JSON.parse(rawTa) : (rawTa || {}); } catch { ta = {}; }
        const roles: number[] = Array.isArray(ta.roles) ? ta.roles : [];
        const specificUserIds: string[] = Array.isArray(ta.specificUsers) ? ta.specificUsers : [];
        const specificEmails: string[] = Array.isArray(ta.specificEmails) ? ta.specificEmails : [];

        const deliveryMethod = campaign.deliveryMethod || campaign.delivery_method || 'both';
        const status = campaign.status || 'draft';
        const sentAt = campaign.sentAt || campaign.sent_at || '';
        const scheduledFor = campaign.scheduledFor || campaign.scheduled_for || '';
        const createdAt = campaign.createdAt || campaign.created_at || '';
        const updatedAt = campaign.updatedAt || campaign.updated_at || '';
        const totalSent = campaign.totalSent ?? campaign.total_sent ?? 0;
        const totalOpened = campaign.totalOpened ?? campaign.total_opened ?? 0;
        const totalClicked = campaign.totalClicked ?? campaign.total_clicked ?? 0;

        return {
          id: campaign.id?.toString() || campaign.uuid,
          uuid: campaign.uuid,
          title: campaign.title,
          subtitle: (campaign.message?.substring(0, 30) || '') + '...',
          type: deliveryMethod,
          audiences: roles.map(roleName),
          status,
          // Sent → when sent; Scheduled (or a draft that has a scheduled_for) →
          // that schedule time; a plain draft → its created date. Never the
          // edit time.
          date: formatDateIST(sentAt || scheduledFor || createdAt),
          dateLabel: status === 'sent' ? 'Sent' :
                     status === 'scheduled' ? 'Scheduled' : 'Draft',
          openRate: totalOpened && totalSent ? Math.round((totalOpened / totalSent) * 100) : undefined,
          clickRate: totalClicked && totalSent ? Math.round((totalClicked / totalSent) * 100) : undefined,
          message: campaign.message,
          targetAudiences: roles.map((r: number) => ({ name: roleName(r), color: '#f59e0b' })),
          sentDate: formatDateIST(sentAt),
          scheduledDate: formatDateIST(scheduledFor),
          scheduledForRaw: scheduledFor || '',
          roles,
          specificUserIds,
          specificEmails,
        };
      });

      return { success: true, data: records, error: null };
    }

    return { success: false, data: [], error: { message: response.error?.message || 'Failed to fetch campaigns' } };
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to fetch notification history' } };
  }
};

export const createCampaign = async (campaignData: CampaignData): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<any>(ENDPOINTS.CAMPAIGNS, campaignData);
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to create campaign' } };
  }
};

export const sendCampaign = async (uuid: string): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<any>(ENDPOINTS.SEND_CAMPAIGN(uuid), {});
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to send campaign' } };
  }
};

export const updateCampaign = async (uuid: string, data: Partial<CampaignData>): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.put<any>(`${ENDPOINTS.CAMPAIGNS}/${uuid}`, data);
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to update campaign' } };
  }
};

export const deleteCampaign = async (uuid: string): Promise<{
  success: boolean;
  data?: any;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.delete<any>(`${ENDPOINTS.CAMPAIGNS}/${uuid}`);
    return response;
  } catch (error: any) {
    return { success: false, error: { message: error?.message ?? 'Failed to delete campaign' } };
  }
};
